import React from 'react';

// One-accent theming: every accent shade of the Caller ID feature derives from a
// single hex. Derivation ratios mirror the design handoff theming table
// (design/design_handoff_caller_id_mobile/README.md).

// Feature accent. Shop-setting integration deferred; changing this hex
// re-derives every accent shade.
export const CALLER_ID_THEME = '#0E8A16';

export interface AccentTokens {
  acc: string;
  accDeep: string;
  accDeep2: string;
  accDeeper: string;
  accSoft: string;
  accBorder: string;
  accDisabled: string;
  accGhost: string;
  accLight: string;
  accShadow: string;
}

const parseHex = (hex: string): [number, number, number] => {
  const h = hex.replace('#', '');
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
};

// darken: each RGB channel × (1 - ratio)
const darken = (hex: string, ratio: number): string => {
  const [r, g, b] = parseHex(hex);
  const m = (x: number) => Math.round(x * (1 - ratio));
  return `rgb(${m(r)},${m(g)},${m(b)})`;
};

// mix toward white: channel + (255 - channel) × ratio
const mixWhite = (hex: string, ratio: number): string => {
  const [r, g, b] = parseHex(hex);
  const m = (x: number) => Math.round(x + (255 - x) * ratio);
  return `rgb(${m(r)},${m(g)},${m(b)})`;
};

const alpha = (hex: string, a: number): string => {
  const [r, g, b] = parseHex(hex);
  return `rgba(${r},${g},${b},${a})`;
};

export const deriveAccentTokens = (theme: string): AccentTokens => ({
  acc: theme,
  accDeep: darken(theme, 0.14),
  accDeep2: darken(theme, 0.28),
  accDeeper: darken(theme, 0.62),
  accSoft: mixWhite(theme, 0.85),
  accBorder: mixWhite(theme, 0.42),
  accDisabled: mixWhite(theme, 0.55),
  accGhost: alpha(theme, 0.08),
  accLight: mixWhite(theme, 0.5),
  accShadow: alpha(theme, 0.38),
});

// CSS custom properties applied on the Caller ID overlay root; feature SCSS
// consumes only these vars for accent colors.
export const accentCssVars = (theme: string): React.CSSProperties => {
  const t = deriveAccentTokens(theme);
  return {
    '--cid-acc': t.acc,
    '--cid-acc-deep': t.accDeep,
    '--cid-acc-deep2': t.accDeep2,
    '--cid-acc-deeper': t.accDeeper,
    '--cid-acc-soft': t.accSoft,
    '--cid-acc-border': t.accBorder,
    '--cid-acc-disabled': t.accDisabled,
    '--cid-acc-ghost': t.accGhost,
    '--cid-acc-light': t.accLight,
    '--cid-acc-shadow': t.accShadow,
  } as React.CSSProperties;
};
